<script setup lang="ts">
import Cloud1 from "@/shared/svg/Cloud1.vue";
import Cloud2 from "@/shared/svg/Cloud2.vue";
import Cloud3 from "@/shared/svg/Cloud3.vue";
import { onMounted } from "vue";
import Button from "@/shared/ui/Button/Button.vue";
import { useRouter } from "vue-router";
import Star from "@/shared/svg/Star.vue";
import 'vue-loading-overlay/dist/css/index.css';

const router = useRouter();

const goToPreorders = () => {
  router.push("/preorders");
}

onMounted(() => {
  setTimeout(() => {
    const clouds = document.querySelectorAll('.cloud');
    clouds.forEach(cloud => cloud.classList.add('cloud--animate'));
  }, 100);
});
</script>

<template>
  <div class="home-page">
    <Cloud1 class="cloud cloud--1" />
    <Cloud2 class="cloud cloud--2" />
    <Cloud3 class="cloud cloud--3" />
    <Star class="star star--1" />
    <Star class="star star--2"/>

    <div class="hero-container">
      <h1 class="hero__text">
        <span class="hero__line">Лавка</span>
        <span class="hero__line">поднебесной</span>
      </h1>
      <Button class="hero__button" variant="outline-filled" size="large" @click="goToPreorders">
        Перейти к предзаказам
      </Button>
    </div>

    <div class="about-section">
      <div class="about-content">
        <div class="about-info">
          <h1 class="about-info__title">Добро<br>пожаловать!</h1>
          <p class="about-info__text">
            Даём возможность прикоснуться к миру Ваших любимых историй и сделать их частью своей жизни.
          </p>
        </div>
      </div>
    </div>
    <div class="how-we-work">
      <h2 class="how-we-work__title">Как мы работаем</h2>
      <picture>
        <source media="(max-width: 768px)" srcset="/images/home/steps_mobile.webp">
        <img class="how-we-work__image" src="/images/home/steps_desktop.webp" loading="lazy">
      </picture>
    </div>
  </div>
</template>

<style scoped lang="scss">
.home-page {
  position: relative;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  min-height: 100vh;

  .cloud {
    position: absolute;
    width: clamp(300px, 40vw, 800px);
    opacity: 0;
    transition: all 2.5s cubic-bezier(0.17, 0.67, 0.21, 0.99);
    z-index: 3;
    pointer-events: none;

    &--animate {
      opacity: 1;
    }

    &--1 {
      top: 2%;
      left: -600px;

      &.cloud--animate {
        left: clamp(-300px, -20vw, -100px);
      }
    }

    &--2 {
      top: 10%;
      left: -600px;

      &.cloud--animate {
        left: clamp(-350px, -25vw, -150px);
      }
    }

    &--3 {
      top: 8%;
      right: -600px;
      transform: translateY(-50%);

      &.cloud--animate {
        right: clamp(-200px, -10vw, -50px);
      }
    }
  }

  .star {
    position: absolute;
    width: clamp(20px, 3vw, 40px);
    height: clamp(20px, 3vw, 40px);
    z-index: 4;
    pointer-events: none;
    opacity: 0;
    transition: all 1s ease;
    animation: twinkle 3s infinite alternate;

    &--1 {
      top: 10%;
      left: clamp(5%, 10%, 10%);
    }

    &--2 {
      top: 10%;
      right: clamp(5%, 15%, 15%);
    }

    &.star--animate {
      opacity: 1;
    }
  }

  @keyframes twinkle {
    0% { opacity: 0.7; transform: scale(0.6); }
    100% { opacity: 1; transform: scale(1.3); }
  }

  .hero-container {
    width: clamp(300px, 80vw, 40vw);
    height: clamp(300px, 80vw, 40vw);
    border-radius: 100%;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    padding-bottom: clamp(30px, 10vw, 129px);
    gap: clamp(24px, 5vw, 48px);
    margin: 20px 0;

    .hero__text {
      color: var(--black);
      text-align: center;
      user-select: none;
      font-size: clamp(1.8rem, 3vw, 2.5rem);;
      letter-spacing: 1px;
      display: flex;
      flex-direction: column;
      margin-top: 20px;
      line-height: 1.2;
      gap: 8px;
    }

    .hero__line {
      display: block;
    }

    .hero__button {
      margin: 0 auto;
      width: clamp(200px, 70%, 40%);
      font-size: clamp(0.8rem, 3vw, 1rem);
    }
  }

  .about-section {
    position: relative;
    width: 102%;
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
    padding: clamp(30px, 5vw, 110px) 20px;
    z-index: 2;
    margin-top: -200px;
    background-image: url(/images/home/full.webp);

    @media (max-width: 1024px) {
      background-image: url(/images/home/full.webp);
      background-size: cover;
      min-height: 400px;
      margin-top: -40px;
    }

    @media (max-width: 768px) {
      background-image: url(/images/home/low.webp);
      background-size: cover;
    }

    @media (max-width: 480px) {
      background-image: url(/images/home/low.webp);
      background-size: contain;
      width: 160%;
      margin-top: -80px;
    }
  }

  .about-content {
    display: flex;
    flex-direction: column;
    position: relative;
    z-index: 2;
    max-width: 860px;
    margin: 0 auto;
    padding-top: clamp(150px, 40vh, 340px);
    gap: clamp(20px, 5vw, 80px);
  }

  .about-info {
    display: flex;
    flex-direction: column;
    justify-content: center;
    margin: 100px auto;
    width: 480px;
    @media (min-width: 1920px) {
      margin-top: 180px;
      width: 480px;
    }
    @media (max-width: 1024px) {
      margin: 20px auto;
      width: 480px;
    }
    @media (max-width: 768px) {
      align-items: center;
      margin-top: -80px;
    }

    &__title {
      font-size: clamp(1.5rem, 4vw, 2rem);
      color: var(--black);
      margin-bottom: clamp(15px, 3vw, 30px);
      text-align: center;
    }

    &__text {
      font-size: clamp(1rem, 3vw, 1.5rem);
      line-height: 1.6;
      color: var(--black);
      text-align: center;
      @media (max-width: 768px) {
        width: 60%;
      }
    }
  }

  .how-we-work {
    position: relative;
    width: 100%;
    padding-top: 128px;
    z-index: 1;

    &__title {
      text-align: center;
      font-size: clamp(1.5rem, 5vw, 2.5rem);
      margin-bottom: clamp(30px, 6vw, 60px);
      color: var(--black);
    }

    &__image {
      position: relative;
      width: 100%;
      height: 100%;
    }

    @media (max-width: 768px) {
      padding-top: 48px;
      margin-bottom: 80px;
    }
  }

  @media (min-width: 768px) {
    .about-content {
      flex-direction: row;
      padding-top: 320px;
      padding-bottom: 20px;
      margin-bottom: 100px;
    }

    .about-info {
      order: initial;
      text-align: center;
      margin-top: -100px;
      width: 240px;

      &__title{
        font-size: 24px;
        text-align: center;
      }
      &__text {
        font-size: 16px;
      }
    }
  }

  @media (max-width: 768px) {
    .about-content {
      flex-direction: row;
      padding-top: 110px;
    }

    .about-info {
      order: initial;
      text-align: center;
      margin-top: 10px;

      &__title{
        font-size: 20px;
        text-align: center;
      }
      &__text {
        font-size: 16px;
        text-align: center;
      }
    }
  }

  @media (min-width: 1921px) {
    .about-content {
      flex-direction: row;
      padding-top: 370px;
      padding-bottom: 100px;
    }

    .about-info {
      order: initial;
      text-align: center;
      margin-top: 40px;
      width: 500px;

      &__title {
        text-align: center;
        font-size: 42px;
      }
      &__text {
        text-align: center;
        font-size: 26px;
      }
    }
  }

  @media (min-width: 1024px) and (max-width: 1920px) {
    .about-content {
      flex-direction: row;
      padding-top: 450px;
      padding-bottom: 0;
      margin-bottom: 50px;
    }

    .about-info {
      order: initial;
      text-align: center;
      padding-bottom: 100px;
      width: 500px;

      &__title {
        text-align: center;
        font-size: 32px;
      }
      &__text {
        text-align: center;
        font-size: 24px;
      }
    }
  }

  @media (max-width: 480px) {
    .hero-container {
      width: 90vw;
      height: 90vw;
    }

    .about-info {
      order: initial;
      text-align: center;
      width: 280px;
      margin-top: 28px;

      &__title{
        font-size: 16px;
      }
      &__text{
        font-size: 12px;
        line-height: normal;
      }
    }

    .cloud {
      width: 80vw;

      &--1.cloud--animate {
        left: -30vw;
      }

      &--2.cloud--animate {
        left: -35vw;
      }

      &--3.cloud--animate {
        right: -20vw;
      }
    }
  }
}

@media (max-width: 1024px) {

  .cloud {
    &--1 {
      top: 1% !important;
      left: -5% !important;
    }
    &--2 {
      top: 4% !important;
      left: -18% !important;
    }
    &--3 {
      top: 4% !important;
      right: -12% !important;
    }
  }

  .star {
    &--1, &--2 {
      top: clamp(5%, 8%, 12%) !important;
    }
  }
}

@media (max-width: 768px) {
  .hero-container {
    margin-top: clamp(30px, 8vh, 80px);
  }

  .cloud {
    width: 70vw;

    &--1 {
      top: 0 !important;
      left: -140px !important;
    }
    &--2 {
      top: 5% !important;
    }
    &--3 {
      top: 4% !important;
      right: -120px !important;
    }
  }

  .star {
    &--1, &--2 {
      top: clamp(5%, 8%, 12%) !important;
    }
  }
}

@media (max-width: 480px) {

  .hero-container {
    margin-top: clamp(40px, 10vh, 100px);
  }

  .cloud {
    width: 70vw;

    &--1 {
      top: 2% !important;
    }
    &--2 {
      top: 260px !important;
    }
    &--3 {
      top: 60px !important;
      right: -120px !important;
    }
  }
}

.fade-enter-active,
.fade-leave-active {
  transition: all 0.8s ease;
}
.fade-enter-from {
  opacity: 0;
  transform: translateY(50px);
}
.fade-leave-to {
  opacity: 0;
}
</style>
